import PropTypes from 'prop-types';
import { useState, useRef, useEffect} from 'react';
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Typography,
  IconButton,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';
import { fToNow } from '../../utils/formatTime';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import MenuPopover from '../../components/MenuPopover';
import axios from 'axios';
import { noCase } from 'change-case';
import { useNavigate } from 'react-router-dom'; // Import React Router's useNavigate

export default function NotificationsPopover() {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [totalUnRead, setTotalUnRead] = useState(0);
  const [open, setOpen] = useState(null);
  const [userId, setUserId] = useState(null);
  const [error, setError] = useState('');

  // Get userId on mount
  useEffect(() => {
    let isMounted = true;
  
    axios
      .get('https://ipms.insollex.com/api/user', { withCredentials: true })
      .then((res) => {
        console.log('User API response:', res.data); // 👈 Add this
        if (isMounted && res.data?.id) {
          setUserId(res.data.id);
          setError('');
        } else {
          setError('User ID is missing in response');
        }
      })
      .catch((err) => {
        console.error('User fetch error:', err); // 👈 Add this
        if (isMounted) setError('User not logged in or failed to fetch');
      });
  
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch notifications function (declared outside useEffect)
  const fetchNotifications = async () => {
    if (!userId) return; // safety check

    try {
      const response = await axios.get('https://ipms.insollex.com/api/notifications', {
        params: { userId },
      });

      setNotifications(response.data.notifications);
      setTotalUnRead(response.data.notifications.filter((n) => n.isUnRead).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  // Fetch notifications whenever userId changes or popover opens
  useEffect(() => {
    if (userId && open) {
      fetchNotifications();
    }
  }, [userId, open]);

  // Mark all notifications as read
  const handleMarkAllAsRead = async () => {
    try {
      await axios.put('https://ipms.insollex.com/api/notifications/markAllAsRead', null, {
        params: { userId },
      });

      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isUnRead: false }))
      );
      setTotalUnRead(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Open the notification popover
  const handleOpen = (event) => {
    setOpen(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setOpen(null);
  };

  // Navigate to notifications page
  const handleViewAll = () => {
    navigate('/NotificationsPage');
    handleClose();
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButton>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              You have {totalUnRead} unread messages
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          <List disablePadding>
            {notifications.length === 0 ? (
              <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                No notifications available.
              </Typography>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                />
              ))
            )}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple onClick={handleViewAll}>
            View All
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    createdAt: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    isUnRead: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    avatar: PropTypes.any,
  }),
};

function NotificationItem({ notification }) {
  const { avatar, title } = renderContent(notification);

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(notification.isUnRead && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {fToNow(new Date(notification.createdAt))}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <Typography variant="subtitle2">
      {notification.title}
      <Typography
        component="span"
        variant="body2"
        sx={{ color: 'text.secondary' }}
      >
        &nbsp;{noCase(notification.description)}
      </Typography>
    </Typography>
  );

  switch (notification.type) {
    case 'password_reset':
      return {
        avatar: <Iconify icon="eva:lock-fill" width={24} height={24} />,
        title,
      };
    case 'profile_update':
      return {
        avatar: <Iconify icon="eva:person-fill" width={24} height={24} />,
        title,
      };
    case 'email_received':
      return {
        avatar: <Iconify icon="eva:email-fill" width={24} height={24} />,
        title,
      };
    default:
      return {
        avatar: null,
        title,
      };
  }
}
